import { OWN_ACCOMMODATION_ROOM_TYPE } from './itinerary';
import { SEGMENT_TYPE } from './segmentTypes';

export const isServiceOnDepartureDay = (dayOfService = 1, nights = 1) =>
  dayOfService > nights ? { OnDepartureDay: true } : { OnDepartureDay: false };

// The system a stay was booked through. A DigiTrips stay's supplierCode is its DigiTrips
// accommodation code and has no Camps record.
// Frozen so the generated declaration carries the string literals rather than
// widening every member to string.
export const SEGMENT_SOURCE = Object.freeze({
  DIGITRIPS: 'digitrips',
});

/**
 * @param {{ source?: string } | null | undefined} segment
 * @returns {boolean}
 */
export const isDigitripsStay = segment => segment?.source === SEGMENT_SOURCE.DIGITRIPS;

/**
 * @param {{ arrangedBy?: string | number }} segment
 * @returns {boolean}
 */
export const isDmcArranged = segment => segment.arrangedBy === 'DMC';
/**
 * @param {{ arrangedBy?: string | number, roomType?: string }} segment
 * @returns {boolean}
 */
export const isOwnArranged = segment => segment.arrangedBy === 'Agent' || segment.roomType === OWN_ACCOMMODATION_ROOM_TYPE;
export const serviceTypeLookUp = {
  VG: 'Vehicle and Driver',
  TR: 'Transfer',
  SS: 'Sightseeing',
  PV: 'Private Vehicle',
  ML: 'Meal',
  MG: 'Meet and Greet',
  GD: 'Guide',
  EF: 'Entrance Fees',
  ET: 'Extras',
  DM: 'DMC',
  SU: 'Sundry',
  BB: 'Bush Buddy',
  CT: 'Charity',
  FE: 'Fee',
  PK: 'Packages',
  BF: 'Booking Fee',
  RE: 'Restaurant',
  TN: 'Train',
  UO: 'Under Own Arrangement',
  CO: 'Coach',
  IT: 'Inclusive Tour',
  IN: 'Insurance',
  EX: 'Explorations-DO NOT',
  CC: 'Cancelled with cost',
  WR: 'Water Rafting',
  CA: 'Car Hire',
  MO: 'Mobile Safari',
  CR: 'Cruises',
  DV: 'Diving',
  TC: 'Team Costs',
  OTHER: 'Other',
};

/**
 * @param {{ supplierCode?: string }} segment
 * @returns {string}
 */
const serviceCodeToServiceType = segment => {
  if (segment.supplierCode && segment.supplierCode.length >= 5) {
    let val;
    try {
      const serviceCode = segment.supplierCode.toUpperCase().slice(3, 5);
      val = serviceTypeLookUp[serviceCode] ?? 'Other';
    } catch {
      val = 'Other';
    } finally {
      return val;
    }
  }
  return 'Other';
};

// Two-letter service codes (supplierCode[3:5]) hidden from clients by default on a fresh quote.
// Keyed on codes, not serviceTypeLookUp labels, so a label edit cannot silently change the rule.
const AUTO_HIDDEN_SERVICE_CODES = ['EF', 'DM', 'ET', 'SU'];

/**
 * @param {{ type?: string, isPackageBookingLine?: boolean, supplierCode?: string } | null | undefined} segment
 * @returns {boolean}
 */
export const shouldAutoHideServiceSegment = segment => {
  if (segment?.type !== SEGMENT_TYPE.SERVICE) return false; // excludes the type:'price' DMC-override line
  if (segment?.isPackageBookingLine) return false; // package/fuel surcharge lines stay visible
  const code = segment?.supplierCode;
  if (typeof code !== 'string' || code.length < 5) return false;
  return AUTO_HIDDEN_SERVICE_CODES.includes(code.toUpperCase().slice(3, 5));
};
/**
 * @param {{ type?: string, linkedSystemId?: string, supplierCode?: string, source?: string, supplierName?: string }} segment
 * @param {{ region?: string, country?: string } | null} [data]
 * @returns {string}
 */
export const segmentTitle = (segment, data = {}) => {
  switch (segment.type) {
    case SEGMENT_TYPE.ROAD:
      switch (segment.linkedSystemId) {
        case SEGMENT_TYPE.BOAT:
          return 'Boat Transfer';
        case SEGMENT_TYPE.HELICOPTER:
          return 'Helicopter Transfer';
        case SEGMENT_TYPE.TRAIN:
          return 'Train Transfer';
        case SEGMENT_TYPE.WALK:
          return 'Walk Transfer';
        default:
          return 'Road Transfer';
      }
    case SEGMENT_TYPE.FLIGHT:
      return 'Flight';
    case SEGMENT_TYPE.STAY:
      if (data?.region && data.country) return `Accommodation in ${data.region}, ${data.country}`;
      // A DigiTrips stay's location is already on the segment, so a missing part is absent, not loading.
      if (isDigitripsStay(segment)) return data?.region ? `Accommodation in ${data.region}` : segment.supplierName;
      return 'Fetching';
    case SEGMENT_TYPE.POINT:
      return 'Transit Point';

    default:
      return serviceCodeToServiceType(segment);
  }
};
